/**
 * `RealBackendBanner` (WP-14).
 *
 * Tokenized Material 3 banner that surfaces two complementary states for
 * the scene-items mutation surface:
 *
 *   1. **Mock SDK active** (`VITE_GTWIN_SCENE_ITEMS=mock`): a dismissible
 *      `info` banner that tells a developer the project is running
 *      against the in-memory mock backend (edits do not persist across
 *      a reload).
 *   2. **Real backend rejected / unreachable** (`VITE_GTWIN_SCENE_ITEMS=real`):
 *      a `warning` (or `error` for 5xx) banner that translates the
 *      typed error stored on `useSyncStatusStore.realBackendError` into
 *      design-policy-compliant copy with an error code and optional
 *      retry action.
 *
 * Both states ship colour **and** a non-colour cue (Material Symbol +
 * label + textual error code) per `DESIGN_POLICY.md` §5. The banner is
 * mounted by `TwinShell` in the header banner column.
 */

import { useEffect, useState } from 'react';
import { Banner, type BannerProps } from '@/design';
import {
  useSyncStatusStore,
  type RealBackendError,
  type RealBackendErrorKind,
} from '@/state/useSyncStatusStore';
import type { SceneItemsMode } from '@/sdk-stub/gtwin-sdk';

export interface RealBackendBannerProps {
  /** The active scene-items mode; usually `gtwinClient.sceneItemsMode`. */
  readonly mode: SceneItemsMode;
  /**
   * Optional override for the error slice (used by tests / Storybook).
   * When omitted the banner subscribes to `useSyncStatusStore`.
   */
  readonly forcedError?: RealBackendError | null;
  /** Optional retry callback for the 5xx + offline banners. */
  readonly onRetry?: () => void;
  /** Optional reload callback for the stale-parent banner. */
  readonly onReload?: () => void;
  readonly 'data-testid'?: string;
}

interface BannerFace {
  readonly severity: BannerProps['severity'];
  readonly icon: BannerProps['icon'];
  readonly title: string;
  readonly body: (error: RealBackendError) => string;
  readonly primaryActionLabel?: string;
}

const FACE_BY_KIND: Record<RealBackendErrorKind, BannerFace> = {
  'stale-parent': {
    severity: 'warning',
    icon: 'sync_problem',
    title: 'Scene was edited by someone else',
    body: () =>
      'Your last edit conflicted with a newer save. Reload the project ' +
      'and re-apply your changes.',
    primaryActionLabel: 'Reload',
  },
  'anchor-conflict': {
    severity: 'warning',
    icon: 'sync_problem',
    title: 'Layer CRS conflicts with project anchor',
    body: (error) =>
      error.attemptedCrs
        ? `The layer's CRS "${error.attemptedCrs}" does not match the ` +
          'promoted project anchor. Reproject the layer first.'
        : "The layer's CRS does not match the promoted project anchor. " +
          'Reproject the layer first.',
  },
  invalid: {
    severity: 'warning',
    icon: 'error',
    title: 'Scene item rejected by the platform',
    body: (error) =>
      error.fieldPath ? `${error.fieldPath}: ${error.message}` : error.message,
  },
  permission: {
    severity: 'warning',
    icon: 'lock',
    title: 'Insufficient permissions',
    body: () =>
      'Your tenant does not have scene-write access on this project. ' +
      'Contact a project owner to grant access.',
  },
  'not-found': {
    severity: 'warning',
    icon: 'error',
    title: 'Project not found',
    body: (error) => error.message,
  },
  server: {
    severity: 'error',
    icon: 'error',
    title: 'Platform backend unavailable',
    body: (error) =>
      error.httpStatus
        ? `The gTwin service rejected the request with HTTP ${error.httpStatus}. ` +
          'Try again, or contact support if the problem keeps happening.'
        : 'The gTwin service rejected the request. ' +
          'Try again, or contact support if the problem keeps happening.',
    primaryActionLabel: 'Retry',
  },
  offline: {
    severity: 'warning',
    icon: 'cloud_off',
    title: 'Real backend not reachable',
    body: () =>
      "Couldn't reach the gTwin platform. Edits are paused until the " +
      'connection is restored.',
    primaryActionLabel: 'Retry',
  },
};

export function RealBackendBanner({
  mode,
  forcedError,
  onRetry,
  onReload,
  'data-testid': testId,
}: RealBackendBannerProps) {
  const storeError = useSyncStatusStore((s) => s.realBackendError);
  const clearRealBackendError = useSyncStatusStore(
    (s) => s.clearRealBackendError,
  );
  const error = forcedError === undefined ? storeError : forcedError;

  // The mock banner is dismissible for the lifetime of the session so a
  // developer can hide it once the message has been read. We track
  // dismissal in component state because the mock banner is a debug
  // affordance, not real telemetry.
  const [mockDismissed, setMockDismissed] = useState(false);
  // Reset dismissal whenever the mode flips back to `mock` from `real`.
  useEffect(() => {
    if (mode === 'mock') return;
    setMockDismissed(false);
  }, [mode]);

  if (mode === 'real' && error) {
    const face = FACE_BY_KIND[error.kind];
    const actions: BannerProps['actions'] = [];
    if (face.primaryActionLabel) {
      if (error.kind === 'stale-parent' && onReload) {
        actions.push({ label: face.primaryActionLabel, onClick: onReload });
      } else if (
        (error.kind === 'server' || error.kind === 'offline') &&
        onRetry
      ) {
        actions.push({ label: face.primaryActionLabel, onClick: onRetry });
      }
    }
    return (
      <Banner
        severity={face.severity}
        title={face.title}
        body={face.body(error)}
        icon={face.icon}
        narrationKey={`wp14-real-backend:${error.kind}`}
        errorCode={error.errorCode}
        persistent
        actions={actions.length > 0 ? actions : undefined}
        onDismiss={() => clearRealBackendError()}
        data-testid={testId ?? 'real-backend-banner'}
      />
    );
  }

  if (mode === 'mock' && !mockDismissed) {
    return (
      <Banner
        severity="info"
        title="Mock SDK active"
        body={
          'Scene edits stay in-memory and disappear on reload. Set ' +
          'VITE_GTWIN_SCENE_ITEMS=real to write to the platform backend.'
        }
        icon="info"
        narrationKey="wp14-real-backend:mock"
        errorCode="WP14-MOCK"
        persistent
        onDismiss={() => setMockDismissed(true)}
        data-testid={testId ?? 'real-backend-banner'}
      />
    );
  }

  return null;
}
